import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import Editor from "@/components/Editor";
import { toast } from "sonner";
import { Save, Trash2, Eye, ArrowLeft, X, Wand2 } from "lucide-react";
import { isoToDatetimeLocal, datetimeLocalToIso, formatPostDate } from "@/lib/date";

const PreviewModal = ({ open, onClose, title, contentHtml, author, coverPath, coverExternal, youtubeUrl, dateLocal }) => {
  if (!open) return null;
  const cover = coverPath ? fileUrl(coverPath) : (coverExternal || null);
  const ytId = (() => {
    if (!youtubeUrl) return null;
    const m = youtubeUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  })();
  const isoDate = dateLocal ? datetimeLocalToIso(dateLocal) : new Date().toISOString();
  return (
    <div
      data-testid="preview-modal"
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-[#1a1918]/75 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl my-8 bg-[#f9f8f6] border border-[#e5e1d8] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="preview-close"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 p-2 bg-[#f9f8f6]/80 hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
          aria-label="chiudi anteprima"
        >
          <X className="h-4 w-4" />
        </button>
        <article className="px-5 sm:px-10 py-8 sm:py-10">
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#9e4747] mb-2">Anteprima</p>
          <p className="font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#4a4843] mb-3">
            {formatPostDate(isoDate)}
          </p>
          <h2 className="font-serif text-2xl sm:text-4xl tracking-tight leading-[1.1] font-light text-[#1a1918]">
            {title || "(senza titolo)"}
          </h2>
          {author && (
            <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mt-2">
              di {author}
            </p>
          )}
          <div className="w-10 h-px bg-[#d4b886] my-6 sm:my-8" />
          {cover && (
            <img src={cover} alt="" className="w-full h-auto mb-6 sm:mb-8" />
          )}
          {!cover && ytId && (
            <div className="mb-6 sm:mb-8 mx-auto max-w-[500px]">
              <div className="aspect-video w-full">
                <iframe
                  src={`https://www.youtube.com/embed/${ytId}`}
                  title={title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          )}
          <div
            className="prose-dialog"
            dangerouslySetInnerHTML={{ __html: contentHtml || "<p><em>Nessun contenuto.</em></p>" }}
          />
        </article>
      </div>
    </div>
  );
};

const AdminEdit = () => {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [coverPath, setCoverPath] = useState(null);
  const [coverExternal, setCoverExternal] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [author, setAuthor] = useState("");
  const [isReserved, setIsReserved] = useState(false);
  const [reservedFor, setReservedFor] = useState("");
  const [users, setUsers] = useState([]);
  const [state, setState] = useState("draft"); // 'draft' | 'published' | 'review'
  const [dateLocal, setDateLocal] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data.items || []);
      } catch (e) {
        // ignore — dropdown will be empty
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/posts/${id}`);
        setTitle(data.title || "");
        setContentHtml(data.content_html || "");
        setCoverPath(data.cover_image_path || null);
        setCoverExternal(data.cover_external_url || null);
        setYoutubeUrl(data.youtube_url || "");
        setAuthor(data.author || "");
        setIsReserved(!!data.is_reserved);
        setReservedFor(data.reserved_for || "");
        if (data.needs_review) setState("review");
        else if (data.published) setState("published");
        else setState("draft");
        setDateLocal(isoToDatetimeLocal(data.created_at));
      } catch (e) {
        toast.error("Errore caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const save = async (overrides = {}) => {
    if (!title.trim()) return toast.error("Inserisci un titolo");
    setSaving(true);
    try {
      const payload = {
        title,
        content_html: contentHtml,
        cover_image_path: coverPath || null,
        cover_external_url: coverPath ? null : (coverExternal || null),
        youtube_url: youtubeUrl || null,
        author: author.trim() || null,
        published: state === "published",
        needs_review: state === "review",
        is_reserved: isReserved,
        reserved_for: isReserved ? (reservedFor || null) : null,
        created_at: datetimeLocalToIso(dateLocal) || null,
        ...overrides,
      };
      if (isNew) {
        const { data } = await api.post("/admin/posts", payload);
        toast.success("Creato");
        nav(`/admin?status=${state}`);
      } else {
        await api.put(`/admin/posts/${id}`, payload);
        toast.success("Salvato");
        nav(`/admin?status=${state}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const setStateValue = async (next) => {
    if (next === state) return;
    if (isNew) {
      setState(next);
      return;
    }
    const prev = state;
    setState(next); // optimistic
    try {
      await api.post(`/admin/posts/${id}/state`, { state: next });
      const labels = { draft: "Bozza", published: "Pubblicato", review: "Da controllare" };
      toast.success(labels[next]);
    } catch (e) {
      setState(prev);
      toast.error(e?.response?.data?.detail || "Errore stato");
    }
  };

  const uploadCoverFromFile = async (f) => {
    if (!f) return;
    if (!f.type || !f.type.startsWith("image/")) {
      toast.error("Solo immagini");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    const tid = toast.loading("caricamento copertina…");
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCoverPath(data.path);
      setCoverExternal(null);
      toast.success("Copertina caricata", { id: tid });
    } catch (err) {
      toast.error("Errore upload", { id: tid });
    }
  };

  const onCoverUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await uploadCoverFromFile(f);
    e.target.value = "";
  };

  const onCoverPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type?.startsWith("image/")) {
        e.preventDefault();
        const f = it.getAsFile();
        if (f) await uploadCoverFromFile(f);
        return;
      }
    }
    const files = e.clipboardData?.files;
    if (files && files.length) {
      const img = Array.from(files).find((f) => f.type?.startsWith("image/"));
      if (img) {
        e.preventDefault();
        await uploadCoverFromFile(img);
      }
    }
  };

  const onCoverDrop = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files && files.length) {
      const img = Array.from(files).find((f) => f.type?.startsWith("image/"));
      if (img) await uploadCoverFromFile(img);
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm("Eliminare definitivamente questo post?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Eliminato");
      nav("/admin");
    } catch (e) {
      toast.error("Errore");
    }
  };

  const reformat = async () => {
    if (!id) return toast.error("Salva prima il post");
    if (!window.confirm("Riformattare i capoversi? Ogni riga diventerà un paragrafo. L'operazione è reversibile solo con annulla nell'editor.")) return;
    try {
      const { data } = await api.post(`/admin/posts/${id}/reformat`);
      setContentHtml(data.content_html || "");
      toast.success("Capoversi riformattati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return <AdminShell title="Modifica"><p className="font-sans text-sm text-[#4a4843]">caricamento…</p></AdminShell>;
  }

  const stateSegments = (
    <div
      data-testid="post-state-segments"
      role="radiogroup"
      aria-label="Stato del post"
      className="flex border border-[#1a1918] w-full sm:w-auto"
    >
      {[
        { k: "draft", label: "bozza" },
        { k: "published", label: "pubblicato" },
        { k: "review", label: "da controllare" },
      ].map((s) => {
        const active = state === s.k;
        const tone =
          s.k === "published"
            ? "bg-[#477d55] text-[#f9f8f6]"
            : s.k === "review"
            ? "bg-[#c5a873] text-[#1a1918]"
            : "bg-[#1a1918] text-[#f9f8f6]";
        return (
          <button
            key={s.k}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`state-segment-${s.k}`}
            onClick={() => setStateValue(s.k)}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase border-r last:border-r-0 border-[#1a1918] transition-colors ${
              active ? tone : "bg-transparent text-[#1a1918] hover:bg-[#f2efe9]"
            }`}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );

  const saveBtn = (
    <button
      data-testid="btn-save"
      onClick={() => save()}
      disabled={saving}
      className="flex items-center justify-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#4a4843] transition-colors px-4 sm:px-5 py-2.5 font-sans text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase disabled:opacity-50"
    >
      <Save size={14} /> {saving ? "…" : "salva"}
    </button>
  );

  return (
    <AdminShell
      title={isNew ? "Nuovo post" : "Modifica post"}
      actions={
        <div className="hidden lg:flex items-center gap-2">
          {stateSegments}
          {!isNew && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              data-testid="btn-view-public"
              className="flex items-center justify-center gap-2 px-4 py-2.5 font-sans text-xs tracking-[0.25em] uppercase border border-[#1a1918] hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
            >
              <Eye size={14} /> anteprima
            </button>
          )}
          {saveBtn}
        </div>
      }
    >
      <Link to="/admin" className="inline-flex items-center gap-2 mb-4 sm:mb-8 font-sans text-xs tracking-[0.3em] uppercase text-[#4a4843] hover:text-[#1a1918]">
        <ArrowLeft size={14} /> <span className="hidden sm:inline">torna all'elenco</span><span className="sm:hidden">elenco</span>
      </Link>

      {/* Mobile-only: state segments + save button between back-link and editor */}
      <div className="lg:hidden mb-6 space-y-3" data-testid="mobile-edit-controls">
        {stateSegments}
        <div className="flex gap-2">
          {!isNew && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              data-testid="btn-view-public-mobile"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-sans text-[10px] tracking-[0.25em] uppercase border border-[#1a1918]"
            >
              <Eye size={14} /> anteprima
            </button>
          )}
          <div className="flex-1 [&>button]:w-full">{saveBtn}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-8">
          <input
            data-testid="post-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titolo del post"
            className="w-full bg-transparent border-0 border-b border-[#e5e1d8] py-3 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight focus:outline-none focus:border-[#1a1918] mb-6 sm:mb-8 font-light"
          />
          <Editor value={contentHtml} onChange={setContentHtml} />
        </div>

        <aside className="lg:col-span-4 flex flex-col gap-6 sm:gap-8 mt-6 lg:mt-0">
          <div className="order-3 lg:order-1">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">Immagine di copertina</p>
            <div
              data-testid="cover-dropzone"
              tabIndex={0}
              onPaste={onCoverPaste}
              onDrop={onCoverDrop}
              onDragOver={(e) => e.preventDefault()}
              className="relative outline-none focus:ring-2 focus:ring-[#d4b886]"
            >
              {coverPath || coverExternal ? (
                <div className="relative">
                  <img
                    src={coverPath ? fileUrl(coverPath) : coverExternal}
                    alt="cover"
                    className="w-full aspect-[4/3] object-cover"
                    data-testid="cover-preview"
                  />
                  <button
                    data-testid="cover-remove"
                    onClick={() => { setCoverPath(null); setCoverExternal(null); }}
                    className="absolute top-2 right-2 bg-[#1a1918] text-[#f9f8f6] p-1 hover:bg-[#9e4747]"
                  >
                    <X size={14} />
                  </button>
                  {coverExternal && !coverPath && (
                    <span className="absolute bottom-2 left-2 bg-[#1a1918]/80 text-[#d4b886] px-2 py-1 font-sans text-[9px] tracking-[0.25em] uppercase">
                      auto · casuale
                    </span>
                  )}
                </div>
              ) : (
                <button
                  data-testid="cover-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="w-full aspect-[4/3] border border-dashed border-[#1a1918] hover:bg-[#f2efe9] transition-colors font-sans text-xs tracking-[0.25em] uppercase text-[#4a4843] flex flex-col items-center justify-center gap-2 px-4"
                >
                  <span>+ carica copertina</span>
                  <span className="text-[9px] tracking-[0.25em] normal-case text-[#4a4843]/70">
                    oppure incolla / trascina · pubblicando senza copertina ne assegno una casuale
                  </span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onCoverUpload} data-testid="cover-input" />
            {(coverPath || coverExternal) && (
              <p className="font-sans text-[9px] tracking-[0.25em] uppercase text-[#4a4843]/70 mt-2">
                suggerimento: click sull'anteprima e Ctrl+V per sostituire
              </p>
            )}
          </div>

          <div className="order-2 lg:order-2">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">Autore</p>
            <input
              data-testid="post-author-input"
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="es. Carlo Ferrari"
              className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
            />
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-2">
              Mostrato come terza riga nelle card del blog.
            </p>
          </div>

          <div className="order-2 lg:order-2">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">Visibilità</p>
            <button
              type="button"
              role="switch"
              aria-checked={isReserved}
              data-testid="post-reserved-toggle"
              onClick={() => setIsReserved((v) => !v)}
              className={`w-full flex items-center justify-between gap-3 border px-3 py-3 transition-colors ${
                isReserved
                  ? "border-[#9e4747] bg-[#9e4747]/5"
                  : "border-[#1a1918] hover:bg-[#f2efe9]"
              }`}
            >
              <span className="font-sans text-xs tracking-[0.25em] uppercase text-[#1a1918]">
                {isReserved ? "Riservato" : "Pubblico"}
              </span>
              <span
                className={`relative inline-block w-10 h-5 rounded-full transition-colors ${
                  isReserved ? "bg-[#9e4747]" : "bg-[#d4d0c8]"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[#f9f8f6] rounded-full transition-transform ${
                    isReserved ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>

            {isReserved && (
              <div className="mt-3" data-testid="reserved-for-block">
                <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] mb-2">
                  Lettore destinatario
                </label>
                <select
                  data-testid="post-reserved-for"
                  value={reservedFor}
                  onChange={(e) => setReservedFor(e.target.value)}
                  className="w-full bg-transparent border border-[#1a1918] py-2 px-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
                >
                  <option value="">— scegli un lettore —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))}
                </select>
                {!users.length && (
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#9e4747] mt-2">
                    Nessun lettore creato. Vai in “Lettori” per aggiungerne.
                  </p>
                )}
                {isReserved && !reservedFor && users.length > 0 && (
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#9e4747] mt-2">
                    Senza un lettore selezionato il post non sarà visibile a nessuno.
                  </p>
                )}
              </div>
            )}

            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-2 leading-relaxed">
              {isReserved
                ? "Visibile solo al lettore selezionato dopo l'accesso."
                : "Visibile a tutti i lettori del blog."}
            </p>
          </div>

          <div className="order-1 lg:order-3">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">Data del post</p>
            <input
              data-testid="post-date-input"
              type="datetime-local"
              value={dateLocal}
              step="1"
              onChange={(e) => setDateLocal(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
            />
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-2 leading-relaxed">
              Modifica per aggiustare data e ora mostrata su blog e liste.
            </p>
          </div>

          <div className="order-4 lg:order-4">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-3">Link YouTube (alternativa)</p>
            <input
              data-testid="youtube-input"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=…"
              className="w-full bg-transparent border-0 border-b border-[#1a1918] py-2 font-sans text-sm focus:outline-none focus:border-[#d4b886]"
            />
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843] mt-2 leading-relaxed">
              Se non c'è una copertina, il video apparirà in cima al post.
            </p>
          </div>

          {!isNew && (
            <div className="space-y-3 order-5 lg:order-5">
              <button
                data-testid="btn-reformat"
                onClick={reformat}
                className="w-full flex items-center justify-center gap-2 border border-[#1a1918] text-[#1a1918] hover:bg-[#d4b886] hover:border-[#d4b886] transition-colors py-3 font-sans text-xs tracking-[0.25em] uppercase"
              >
                <Wand2 size={14} /> riformatta capoversi
              </button>
              <button
                data-testid="btn-delete"
                onClick={remove}
                className="w-full flex items-center justify-center gap-2 border border-[#9e4747] text-[#9e4747] hover:bg-[#9e4747] hover:text-[#f9f8f6] transition-colors py-3 font-sans text-xs tracking-[0.25em] uppercase"
              >
                <Trash2 size={14} /> elimina post
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Mobile sticky save bar */}
      <div
        data-testid="mobile-save-bar"        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f9f8f6]/95 backdrop-blur border-t border-[#e5e1d8] px-4 py-3 flex items-center gap-3"
      >
        <span
          data-testid="mobile-state-pill"
          className={`shrink-0 px-2.5 py-1.5 font-sans text-[10px] tracking-[0.25em] uppercase ${
            state === "published"
              ? "bg-[#477d55] text-[#f9f8f6]"
              : state === "review"
              ? "bg-[#c5a873] text-[#1a1918]"
              : "bg-[#1a1918] text-[#f9f8f6]"
          }`}
        >
          {state === "published" ? "pubblicato" : state === "review" ? "da controllare" : "bozza"}
        </span>
        <button
          data-testid="btn-save-sticky"
          onClick={() => save()}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 bg-[#1a1918] text-[#f9f8f6] py-3 font-sans text-xs tracking-[0.25em] uppercase disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "salvataggio…" : "salva"}
        </button>
      </div>
      {/* spacer so content isn't hidden behind sticky bar */}
      <div className="lg:hidden h-20" aria-hidden />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={title}
        contentHtml={contentHtml}
        author={author}
        coverPath={coverPath}
        coverExternal={coverExternal}
        youtubeUrl={youtubeUrl}
        dateLocal={dateLocal}
      />
    </AdminShell>
  );
};

export default AdminEdit;

